import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { locationQueryOptions, updateLocation } from '@/api/resources/locations'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { EditLocationForm } from '@/components/locations/edit-location-form'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute(
  '/_app/organisations/$organisationId/locations_/$locationId/edit',
)({
  head: () => ({ meta: [{ title: 'Edit Location - MyJunea' }] }),
  component: EditLocationRoute,
})

function EditLocationRoute(): React.ReactElement {
  const { organisationId, locationId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/organisations/$organisationId',
        params: { organisationId },
        resetScroll: false,
      }),
  )
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const locationIdNum = Number(locationId)

  const { data: location } = useQuery(locationQueryOptions(locationIdNum))

  const mutation = useMutation({
    mutationFn: async (values: Parameters<typeof updateLocation>[1]) =>
      updateLocation(locationIdNum, values),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.locations.detail(locationIdNum),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.locations.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.all })
      void router.invalidate()
      toast.success('Location updated')
      handleClose()
    },
    onError: handleMutationError,
  })

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        {location === undefined ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-2xl font-extrabold tracking-tight">
                Edit Location
              </SheetTitle>
            </SheetHeader>
            <LoadingSpinner />
          </>
        ) : (
          <EditLocationForm
            key={location.id}
            location={location}
            title="Edit Location"
            onSubmit={(values) => mutation.mutate(values)}
            isPending={mutation.isPending}
            serverErrors={serverErrors}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
